import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { Confirmation, ToasterService } from '@abp/ng.theme.shared';
import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, Input, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, map, startWith, switchMap } from 'rxjs';
import {
  PeriodService,
  ReasonGroupService,
  ReasonService,
  StandAreaMaintenanceTypeService,
  StandService,
} from 'cbd-proxies';
import {
  ActionSelectOption,
  DEFAULT_MAX_RESULT_COUNT,
  PERIOD_TYPE_NAME_SILVICULTURE,
  REASON_GROUP_NAME_AREA_ADJUSTMENT,
  ZnConfirmation,
  ZnSelectQueryParams,
  ZendraLocalizationServices,
  DEFAULT_TOASTER_LIFE,
} from '@zendra/shared';
import { IModalContent } from 'projects/shared/src/lib/zn-modal-wrapper/interfaces/IModalContent';
import { StandAreaMaintenancePlanningOrchestratorService } from 'projects/orchestrator/src/lib/proxy/orchestrator/controllers/stand-area-maintenance-planning-orchestrator.service';
import {
  CreateManyStandAreaMaintenanceInputDto,
  CreateStandAreaMaintenanceForStandDto,
} from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/stand-area-maintenance/models';
import { ZnSingleSelect } from '../../../../../shared/src/lib/zn-select/zn-single-select';
import { StandGis } from '../../stand/stand-gis/stand-gis';

export type ActionCreateStandAreaMaintenanceMode = 'single' | 'bulkFromSelection';

@Component({
  selector: 'lib-action-create-stand-area-maintenance',
  imports: [
    LocalizationPipe,
    CommonModule,
    ReactiveFormsModule,
    ZnSingleSelect,
    StandGis,
  ],
  templateUrl: './action-create-stand-area-maintenance.html',
})
export class ActionCreateStandAreaMaintenance implements IModalContent {
  readonly notesMaxLength = 150;
  private readonly periodSorting = 'StockStartDate desc';

  private readonly formBuilder = inject(FormBuilder);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly standAreaMaintenanceOrchestratorService = inject(
    StandAreaMaintenancePlanningOrchestratorService,
  );
  private readonly standAreaMaintenanceTypeService = inject(StandAreaMaintenanceTypeService);
  private readonly periodService = inject(PeriodService);
  private readonly reasonService = inject(ReasonService);
  private readonly reasonGroupService = inject(ReasonGroupService);
  private readonly standService = inject(StandService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toaster = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);

  readonly selectedRecords = input<any[]>([]);
  readonly selectedRecordIds = input<number[]>([]);
  readonly mode = input<ActionCreateStandAreaMaintenanceMode>('single');
  readonly hideStandField = input(false);
  readonly hideAreaToBeAdjustedField = input(false);
  readonly hideIsAreaUpdatedField = input(false);

  private readonly parentStandLocationIdValue = signal<number | null>(null);
  private readonly parentStandIdValue = signal<number | null>(null);
  private readonly parentStandNameValue = signal('');
  private readonly catalogLoading = signal(true);
  readonly saving = signal(false);
  readonly loading = computed(() => this.catalogLoading() || this.saving());
  readonly reasonOptions = signal<ActionSelectOption[]>([]);
  readonly adjustmentTypeOptions = signal<ActionSelectOption[]>([]);
  readonly isBulkMode = computed(() => this.mode() === 'bulkFromSelection');
  readonly shouldHideStandField = computed(() => this.hideStandField() || this.isBulkMode());
  readonly shouldHideAreaToBeAdjustedField = computed(
    () => this.hideAreaToBeAdjustedField() || this.isBulkMode(),
  );
  readonly shouldHideIsAreaUpdatedField = computed(
    () => this.hideIsAreaUpdatedField() || this.isBulkMode(),
  );

  readonly periodsLoader = (params?: ZnSelectQueryParams) =>
    this.periodService
      .getList({
        periodTypeName: PERIOD_TYPE_NAME_SILVICULTURE,
        name: params?.search,
        sorting: this.periodSorting,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: response.items.map(period => ({
            key: String(period.id),
            value: period.name ?? String(period.id),
          })),
          totalCount: response.totalCount,
        })),
      );

  localizationResource = ZendraLocalizationServices.LanguageService;
  form: FormGroup = this.formBuilder.group({
    periodId: [null, [Validators.required]],
    adjustmentReasonId: [null, [Validators.required]],
    areaToBeAdjusted: [null, [Validators.required, Validators.min(0.01)]],
    isAreaUpdated: [true],
    notes: [null, [Validators.maxLength(this.notesMaxLength)]],
    standAreaMaintenanceTypeId: [null, [Validators.required]],
    standId: [null, [Validators.required, Validators.min(1)]],
  });

  private readonly formStatus = toSignal(
    this.form.statusChanges.pipe(startWith(this.form.status)),
    { initialValue: this.form.status },
  );

  readonly canSave = computed(() => {
    this.formStatus();
    return this.form.valid && !this.loading();
  });

  readonly resolvedParentStandName = computed(() => this.parentStandNameValue().trim());

  constructor() {
    effect(() => {
      this.applyModeConfiguration();
    });

    this.loadCatalogs();
  }

  @Input() set parentStandLocationId(value: number | null | undefined) {
    const normalizedValue = value ?? null;
    this.parentStandLocationIdValue.set(normalizedValue);

    if (normalizedValue === null) {
      this.resetParentStandContext();
      return;
    }

    if (!this.parentStandIdValue() || !this.resolvedParentStandName()) {
      this.loadParentStandContext(normalizedValue);
    }
  }

  @Input() set parentStandId(value: number | null | undefined) {
    const normalizedValue = value ?? null;
    this.parentStandIdValue.set(normalizedValue);
    this.syncParentStandIdControl(normalizedValue);

    const parentStandLocationId = this.parentStandLocationIdValue();
    if (normalizedValue === null && parentStandLocationId !== null) {
      this.loadParentStandContext(parentStandLocationId);
    }
  }

  @Input() set parentStandName(value: string | null | undefined) {
    const normalizedValue = value?.trim() ?? '';
    this.parentStandNameValue.set(normalizedValue);

    const parentStandLocationId = this.parentStandLocationIdValue();
    if ((!normalizedValue || !this.parentStandIdValue()) && parentStandLocationId) {
      this.loadParentStandContext(parentStandLocationId);
    }
  }

  fieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  onUpdateGisArea(area: number): void {
    const control = this.form.get('areaToBeAdjusted');
    control?.setValue(area);
    control?.markAsDirty();
  }

  save(): void {
    if (this.form.invalid || this.loading()) {
      if (this.form.invalid) {
        this.form.markAllAsTouched();
      }
      return;
    }

    if (this.isBulkMode()) {
      const payload = this.buildCreateManyPayload();
      if (!payload) {
        return;
      }

      this.saving.set(true);
      this.standAreaMaintenanceOrchestratorService.createMany(payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.saving.set(false);
            this.notifySuccess();
            this.activeModal.close(response);
          },
          error: error => {
            console.error(error);
            this.saving.set(false);
          },
        });
      return;
    }

    const payload = this.buildCreatePayload();
    this.saving.set(true);
    this.standAreaMaintenanceOrchestratorService.create(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.saving.set(false);
          this.notifySuccess();
          this.activeModal.close(response);
        },
        error: error => {
          console.error(error);
          this.saving.set(false);
        },
      });
  }

  confirm(): void {
    this.save();
  }

  dismiss(): void {
    if (this.saving()) {
      return;
    }

    if (this.form.dirty) {
      this.confirmationService.showCancelConfirmation().subscribe(status => {
        if (status === Confirmation.Status.confirm) {
          this.form.reset(this.getDefaultFormValue());
          this.activeModal.dismiss();
        }
      });
      return;
    }

    this.activeModal.dismiss();
  }

  private notifySuccess(): void {
    this.toaster.success(`${this.localizationResource}::Actions:SuccessMessage`, '', {
      life: DEFAULT_TOASTER_LIFE,
      messageLocalizationParams: [
        this.localizationService.instant(
          `${ZendraLocalizationServices.Geographic}::Action.Modal.createStandAreaMaintenance`,
        ),
      ],
    });
  }

  private applyModeConfiguration(): void {
    if (this.shouldHideAreaToBeAdjustedField()) {
      this.disableControl('areaToBeAdjusted');
    } else {
      this.enableControl('areaToBeAdjusted');
    }

    if (this.shouldHideIsAreaUpdatedField()) {
      this.disableControl('isAreaUpdated');
    } else {
      this.enableControl('isAreaUpdated');
    }

    if (this.shouldHideStandField()) {
      this.disableControl('standId');
      return;
    }

    this.syncParentStandIdControl(this.parentStandIdValue());
  }

  private disableControl(controlName: string): void {
    this.form.get(controlName)?.disable({ emitEvent: false });
  }

  private enableControl(controlName: string): void {
    const control = this.form.get(controlName);
    if (!control?.disabled) {
      return;
    }

    control.enable({ emitEvent: false });
  }

  private getDefaultFormValue() {
    return {
      periodId: null,
      adjustmentReasonId: null,
      areaToBeAdjusted: null,
      isAreaUpdated: true,
      notes: null,
      standAreaMaintenanceTypeId: null,
      standId: this.shouldHideStandField() ? this.form.getRawValue().standId : null,
    };
  }

  private buildCreatePayload(): CreateStandAreaMaintenanceForStandDto {
    const formValue = this.form.getRawValue();

    return {
      standId: Number(formValue.standId),
      periodId: Number(formValue.periodId),
      adjustmentReasonId: Number(formValue.adjustmentReasonId),
      areaToBeAdjusted: Number(formValue.areaToBeAdjusted),
      isAreaUpdated: this.normalizeBoolean(formValue.isAreaUpdated),
      notes: this.normalizeNotes(formValue.notes),
      standAreaMaintenanceTypeId: Number(formValue.standAreaMaintenanceTypeId),
    };
  }

  private buildCreateManyPayload(): CreateManyStandAreaMaintenanceInputDto | null {
    const selectedRecords = this.selectedRecords();
    if (!selectedRecords.length) {
      return null;
    }

    const formValue = this.form.getRawValue();

    return {
      periodId: Number(formValue.periodId),
      adjustmentReasonId: Number(formValue.adjustmentReasonId),
      standAreaMaintenanceTypeId: Number(formValue.standAreaMaintenanceTypeId),
      notes: this.normalizeNotes(formValue.notes),
      items: selectedRecords.map(record => ({
        standId: Number(record.standId),
        standLocationId: Number(record.locationId ?? record.standLocationId),
        currentArea: Number(record.standArea ?? record.currentArea),
        gisArea: Number(record.gisArea),
      })),
    };
  }

  private normalizeNotes(value?: string | null): string | null {
    return value?.trim() ? value.trim() : null;
  }

  private loadCatalogs(): void {
    this.catalogLoading.set(true);

    this.reasonGroupService
      .getList({
        name: REASON_GROUP_NAME_AREA_ADJUSTMENT,
        maxResultCount: 1,
      })
      .pipe(
        map(reasonGroup => {
          const reasonGroupId = reasonGroup.items?.[0]?.id;

          if (!reasonGroupId) {
            throw new Error(`${this.localizationResource}::ReasonGroupNotFound`);
          }

          return reasonGroupId;
        }),
        switchMap(reasonGroupId =>
          forkJoin({
            reasons: this.reasonService.getList({
              reasonGroupId: [reasonGroupId],
              maxResultCount: 100,
            }),
            adjustmentTypes: this.standAreaMaintenanceTypeService.getList({
              maxResultCount: 100,
            }),
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ reasons, adjustmentTypes }) => {
          this.reasonOptions.set(this.toSelectOptions(reasons.items ?? []));
          this.adjustmentTypeOptions.set(
            this.toSelectOptions(adjustmentTypes.items ?? []),
          );
          this.catalogLoading.set(false);
        },
        error: error => {
          console.error(error);
          this.toaster.error(`${this.localizationResource}::ErrorLoadingCatalogs`, '', {
            life: DEFAULT_TOASTER_LIFE,
          });
          this.catalogLoading.set(false);
        },
      });
  }

  private loadParentStandContext(standLocationId: number): void {
    this.standService
      .getList({
        locationId: [standLocationId],
        maxResultCount: 1,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const stand = response.items?.[0];
          const standId = stand?.id ?? null;
          this.parentStandIdValue.set(standId);
          this.syncParentStandIdControl(standId);
          this.parentStandNameValue.set(
            stand?.location?.name?.trim() ?? stand?.name?.trim() ?? '',
          );
        },
        error: error => {
          console.error(error);
        },
      });
  }

  private syncParentStandIdControl(standId: number | null): void {
    const standIdControl = this.form.get('standId');
    if (!standIdControl) {
      return;
    }

    if (this.shouldHideStandField()) {
      standIdControl.disable({ emitEvent: false });
      return;
    }

    if (standId === null) {
      standIdControl.setValue(null, { emitEvent: false });
      standIdControl.enable({ emitEvent: false });
      return;
    }

    standIdControl.setValue(standId, { emitEvent: false });
    standIdControl.disable({ emitEvent: false });
  }

  private resetParentStandContext(): void {
    this.parentStandNameValue.set('');
    this.parentStandIdValue.set(null);
    this.syncParentStandIdControl(null);
  }

  private toSelectOptions<T extends { id?: number; name?: string | null }>(
    collection: T[],
  ): ActionSelectOption[] {
    return collection.flatMap(item =>
      item.id === undefined
        ? []
        : [{
            key: item.id,
            value: item.name ?? '',
          }],
    );
  }

  private normalizeBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    return String(value).toLowerCase() === 'true';
  }
}
